import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import VehicleInterestCard from './VehicleInterestCard';

export interface PublicVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  stock_number?: string;
  asking_price?: number;
  price?: number;
  mileage?: number;
  image?: string;
  bhph_eligible: boolean;
  bhph_min_down_type?: string;
  bhph_min_down_value?: number;
  bhph_payment_amount?: number;
  bhph_payment_frequency?: string;
  bhph_term_months?: number;
}

interface VehicleSelectorProps {
  selectedVehicles: PublicVehicle[];
  onSelectionChange: (vehicles: PublicVehicle[]) => void;
  availableVehicles?: PublicVehicle[];
  fetchUrl?: string;
  placeholder?: string;
}

const VehicleSelector: React.FC<VehicleSelectorProps> = ({
  selectedVehicles,
  onSelectionChange,
  availableVehicles: externalVehicles,
  fetchUrl = '/api/v1/vehicles/public',
  placeholder = 'Search by make, model, or year...',
}) => {
  const [search, setSearch] = useState('');
  const [vehicles, setVehicles] = useState<PublicVehicle[]>(externalVehicles || []);
  const [loading, setLoading] = useState(!externalVehicles);
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (externalVehicles) {
      setVehicles(externalVehicles);
      setLoading(false);
      return;
    }
    const fetchVehicles = async () => {
      try {
        const res = await fetch(fetchUrl);
        if (res.ok) {
          const data = await res.json();
          setVehicles(Array.isArray(data) ? data : data.vehicles || []);
        }
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [fetchUrl, externalVehicles]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedIds = new Set(selectedVehicles.map((v) => v.id));

  const filtered = vehicles.filter((v) => {
    if (selectedIds.has(v.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      v.make?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q) ||
      v.trim?.toLowerCase().includes(q) ||
      String(v.year).includes(q) ||
      v.stock_number?.toLowerCase().includes(q)
    );
  });

  const handleSelect = (vehicle: PublicVehicle) => {
    onSelectionChange([...selectedVehicles, vehicle]);
    setSearch('');
    setShowResults(false);
  };

  const handleRemove = (vehicleId: string) => {
    onSelectionChange(selectedVehicles.filter((v) => v.id !== vehicleId));
  };

  const fmtCurrency = (v?: number) =>
    v != null
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(v)
      : '';

  return (
    <div ref={wrapperRef} className="space-y-2">
      {selectedVehicles.length > 0 && (
        <div className="space-y-1.5">
          {selectedVehicles.map((v) => (
            <VehicleInterestCard key={v.id} vehicle={v} onRemove={() => handleRemove(v.id)} />
          ))}
        </div>
      )}

      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {showResults && (
        <div className="border border-gray-200 rounded-lg bg-white shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500">Loading vehicles...</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {search ? 'No matching vehicles found' : 'All vehicles selected'}
            </div>
          ) : (
            filtered.map((v) => (
              <button
                key={v.id}
                onClick={() => handleSelect(v)}
                className="w-full flex items-center gap-3 p-2 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
              >
                <div className="w-14 h-10 rounded bg-gray-200 overflow-hidden flex-shrink-0">
                  {v.image ? (
                    <img src={v.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px]">
                      No img
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {v.year} {v.make} {v.model} {v.trim || ''}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {v.stock_number && <span>#{v.stock_number}</span>}
                    <span>{fmtCurrency(v.asking_price ?? v.price)}</span>
                    {v.mileage && <span>{v.mileage.toLocaleString()} mi</span>}
                  </div>
                </div>
                {v.bhph_eligible ? (
                  <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-semibold flex-shrink-0">
                    BHPH
                  </span>
                ) : (
                  <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[10px] font-semibold flex-shrink-0">
                    Finance
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleSelector;
